import { useAppSelectorV1 } from "@redux/hooks";
import { NavPoint } from "@redux/navPoint/NavPoint";

import type { RootState } from "@redux/store";
import { parseVersionedIdentifier } from "@redux/utils/identifiers";
import { createSelector } from "reselect";

/**
 * Returns user from state
 */
export const getUser = (state: RootState) => {
  return state.user || null;
};
export const useGetUser = () => useAppSelectorV1(getUser);

/**
 * Returns spaceIds from the user data in state
 */
// export const getUserSpaceIds = (state: RootState) => {
//   return state.user?.spaces || [];
// };

/**
 * Returns custom fields from state entities
 */
export const getAssetViews = (state: RootState) => {
  return state.entities.views.objects;
};

/**
 * Returns custom fields from state entities
 */
export const getCustomFields = (state: RootState) => {
  return state.entities.fields.objects;
};

/**
 * Returns items from state
 */
export const getItems = (state: RootState) => state.entities.items.objects;
export const useGetItems = () => useAppSelectorV1(getItems);
/**
 * Returns spaces from entities in state
 */
export const getSpaces = (state: RootState) => state.entities.spaces.objects;
export const useGetSpaces = () => useAppSelectorV1(getSpaces);

/**
 * Returns modals from state
 */
export const getModals = (state: RootState) => state.modals;
export const useGetModals = () => useAppSelectorV1(getModals);

/**
 * Returns active uploads
 */
export const getUploads = (state: RootState) => state.uploads;
export const useGetUploads = () => useAppSelectorV1(getUploads);

/**
 * Returns kits from entities in state
 */
export const getSections = (state: RootState) => state.entities.sections.objects;
export const getSection = (state: RootState, props?: { identifier: string; version?: number }) => {
  const { identifier, version: versionNumber } = props || {};
  if (!identifier) return null;
  const { id, version } = parseVersionedIdentifier(identifier, versionNumber);
  if (version == null) return null;
  const uuid = state.entities.shortIds?.sections?.[id] || id;
  const idVersion = `${uuid}-${version}`;
  return state.entities.sections.objects[idVersion] || null;
};
export const useGetSections = () => useAppSelectorV1(getSections);

// Portals
export const getPortals = (state: RootState) => state.entities.portals.objects;
export const useGetPortals = () => useAppSelectorV1(getPortals);

/**
 * Returns kits from entities in state
 */
export const getKits = (state: RootState) => state.entities.kits.objects;
export const useGetKits = () => useAppSelectorV1(getKits);

export const getProducts = (state: RootState) => state.entities.products.objects;
export const useGetProducts = () => useAppSelectorV1(getProducts);

export const getProductEdit = (state: RootState) => state.productEdit;
export const useGetProductEdit = () => useAppSelectorV1(getProductEdit);
/**
 * Returns kit versions from entities in state
 */
export const getKitVersions = (state: RootState) => state.entities.kitVersions.objects;

export const getKitVersionId = (
  state: RootState,
  props?: { kitId?: string; version?: number }
): string => {
  if (props?.kitId) {
    return `${props?.kitId}-${props?.version || 0}`;
  }
  return `${state.navPoint?.kitId}-${state.navPoint?.version || 0}`;
};
/**
 * Returns kit members from entities in state
 */
export const getKitMembers = (state: RootState) => state.entities.kitMembers.objects;

/**
 * Returns current version from either props or nav point
 */
export const getVersion = (state: RootState, props?: { version?: number }): number =>
  props?.version || state.navPoint?.version;

/**
 * Returns the space id either from props or from the current nav point
 */
export const getSpaceId = (
  state: RootState,
  props?: { spaceId?: number | string }
): string | number => props?.spaceId || state.navPoint?.spaceId;

/**
 * Returns the section id either from props or from the current nav point
 */
export const getSectionId = (
  state: RootState,
  props?: { sectionId?: string; version?: number }
): string => {
  if (props?.sectionId) {
    const identifier = parseVersionedIdentifier(props.sectionId, props.version);
    return `${identifier.id}-${identifier.version || 0}`;
  }
  return `${state.navPoint?.sectionId}-${state.navPoint?.version || 0}`;
};

/**
 * Returns the kit id either from props or from the current nav point
 */
export const getKitId = (state: RootState, props?: { kitId?: string }): string =>
  props?.kitId || state.navPoint?.kitId;

/**
 * Returns cached assets
 */
export const getAssets = (state: RootState) => state.entities.assets.objects;

export const useGetAssets = () => useAppSelectorV1(getAssets);
/**
 * Returns entire state obj
 */
export const getState = (state: RootState): RootState => state;
export const useGetState = () => useAppSelectorV1(getState);

/**
 * Returns current nav point from state
 */
export const getNavPoint = (state: RootState) => state.navPoint;
export const getNavPointInsightsPage = (state: RootState) => state.navPoint?.insightsPage;
export const useGetNavPointInsightsPage = () => useAppSelectorV1(getNavPointInsightsPage);
export const useGetNavPoint = () => useAppSelectorV1(getNavPoint);

export const selectPopulatedNavPoint = createSelector(
  [
    // Only select the specific entities referenced by navPoint, not entire slices
    // This prevents re-renders when unrelated entities in any slice change
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.spaceId) return null;
      return state.entities.spaces.objects[navPoint.spaceId] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.portalId) return null;
      return state.entities.portals.objects[navPoint.portalId] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.kitId) return null;
      return state.entities.kits.objects[navPoint.kitId] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.kitId) return null;
      const kitVersionKey = [navPoint.kitId, navPoint.version].join("-");
      return state.entities.kitVersions.objects[kitVersionKey] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.sectionId) return null;
      const sectionKey = [navPoint.sectionId, navPoint.version].join("-");
      return state.entities.sections.objects[sectionKey] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.itemId) return null;
      const itemKey = [navPoint.itemId, navPoint.version].join("-");
      return state.entities.items.objects[itemKey] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.galleryId) return null;
      const galleryKey = [navPoint.galleryId, navPoint.version].join("-");
      return state.entities.items.objects[galleryKey] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.assetId) return null;
      return state.entities.assets.objects[navPoint.assetId] ?? null;
    },
    // Also get assets for item and gallery if they have assetIds
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.itemId) return null;
      const itemKey = [navPoint.itemId, navPoint.version].join("-");
      const item = state.entities.items.objects[itemKey];
      if (!item?.assetId) return null;
      return state.entities.assets.objects[item.assetId] ?? null;
    },
    (state: RootState) => {
      const navPoint = state.navPoint;
      if (!navPoint?.galleryId) return null;
      const galleryKey = [navPoint.galleryId, navPoint.version].join("-");
      const gallery = state.entities.items.objects[galleryKey];
      if (!gallery?.assetId) return null;
      return state.entities.assets.objects[gallery.assetId] ?? null;
    },
    getNavPoint,
  ],
  (
    space,
    portal,
    kit,
    kitVersion,
    section,
    navPointItem,
    navPointGallery,
    navPointAsset,
    itemAsset,
    galleryAsset,
    navPoint
  ): NavPoint => {
    if (!navPoint) return null;

    const asset = navPointAsset;

    let item = navPointItem;
    if (item?.assetId && itemAsset) {
      item = { ...item, asset: itemAsset };
    }

    let gallery = navPointGallery;
    if (gallery?.assetId && galleryAsset) {
      gallery = { ...gallery, asset: galleryAsset };
    }

    return {
      ...navPoint,
      space,
      portal,
      kit,
      kitVersion,
      item,
      asset,
      section,
      gallery,
    };
  }
);
